import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  Autocomplete,
} from '@mui/material';

function AddEditWeighbridgeModal({ open, onClose, onSave, destinationList, editData = null }) {
  const [formData, setFormData] = useState({
    id: 0,
    customerName: '',
    destinationName: '',
    destinationSiteAssessmentID: null,
    weighbridgeName: '',
    custCode: '',
    adminEmailAddress: '',
    accountsEmailAddress: '',
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (editData) {
      
      setFormData({
        id: editData.id || 0,
        customerName: editData.customerName || '',
        destinationName: editData.destinationName || '',
        destinationSiteAssessmentID: editData.destinationSiteAssessmentID || 0,
        weighbridgeName: editData.weighbridgeName || '',
        custCode: editData.custCode || '',
        adminEmailAddress: editData.adminEmailAddress || '',
        accountsEmailAddress: editData.accountsEmailAddress || '',
      });
    } else {
      setFormData({
        customerName: '',
        destinationName: '',
        weighbridgeName: '',
        custCode: '',
        adminEmailAddress: '',
        accountsEmailAddress: '',
      });
    }
    setErrors({});
  }, [editData, open]);

  // Auto-generate customer code from customer name and destination name
  const generateCustCode = (customerName, destinationName) => {
    if (!customerName || !destinationName) return '';

    // Extract first few letters from each name (remove special chars, take uppercase)
    const cleanCustomer = customerName.replace(/[^a-zA-Z]/g, '').toUpperCase();
    const cleanDestination = destinationName.replace(/[^a-zA-Z]/g, '').toUpperCase();

    // Take first 4-5 letters from customer and 2-3 from destination
    const customerPart = cleanCustomer.substring(0, Math.min(5, cleanCustomer.length));
    const destinationPart = cleanDestination.substring(0, Math.min(3, cleanDestination.length));

    return `${customerPart}${destinationPart}`;
  };

  const handleChange = (field, value) => {
    const newFormData = { ...formData, [field]: value };

    // Auto-generate custCode when customer or destination changes
    if (field === 'customerName' || field === 'destinationName') {
      newFormData.custCode = generateCustCode(
        field === 'customerName' ? value : formData.customerName,
        field === 'destinationName' ? value : formData.destinationName,
      );
    }

    setFormData(newFormData);

    // Clear error for this field
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.customerName.trim()) {
      newErrors.customerName = 'Customer name is required';
    }

    if (!formData.destinationName.trim()) {
      newErrors.destinationName = 'Destination name is required';
    }

    if (!formData.weighbridgeName.trim()) {
      newErrors.weighbridgeName = 'Weighbridge name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onSave({
        ...formData,
        ...(editData && {
          id: editData.id,
          weighbridgeSiteAssessmentID: editData.weighbridgeSiteAssessmentID,
        }),
      });
    }
  };

  const destinationOptions = React.useMemo(() => {
    const map = new Map();
    if (destinationList?.length > 0) {
      destinationList.forEach(item => {
        if (!map.has(item.destinationName)) {
          map.set(item.destinationName, {
            destinationName: item.destinationName,
            siteAssessmentId: item.siteAssessmentId,
          });
        }
      });
    }
    return Array.from(map.values());
  }, [destinationList]);

  const customerOptions = React.useMemo(() => {
    return Array.from(new Set(destinationList?.map(item => item.customerName)));
  }, [destinationList]);

  console.log("FORM DATA IN MODAL ====", formData);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editData ? 'Edit Weighbridge Details' : 'Add Weighbridge Details'}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Autocomplete
            options={destinationOptions}
            getOptionLabel={option => option.destinationName}
            value={destinationOptions.find(d => d.destinationName === formData.destinationName) || null}
            onChange={(e, newValue) => {
              handleChange('destinationName', newValue?.destinationName || '');
              setFormData(prev => ({
                ...prev,
                destinationSiteAssessmentID: newValue?.siteAssessmentId || null,
              }));
            }}
            disabled={editData}
            renderInput={params => (
              <TextField
                {...params}
                label="Destination"
                required
                error={!!errors.destinationName}
                helperText={errors.destinationName}
              />
            )}
          />

          <Autocomplete
            freeSolo
            options={customerOptions}
            value={formData.customerName}
            onInputChange={(e, newValue) => {
              handleChange('customerName', newValue);
            }}
            renderInput={params => (
              <TextField
                {...params}
                label="Customer Name"
                required
                error={!!errors.customerName}
                helperText={errors.customerName || 'Select or type a customer name'}
              />
            )}
          />

          <TextField
            label="Customer Code"
            value={formData.custCode}
            disabled
            fullWidth
            helperText="Auto-generated from Customer and Destination names"
            InputProps={{
              sx: { backgroundColor: '#f5f5f5' },
            }}
          />

          <TextField
            label="Weighbridge Name"
            value={formData.weighbridgeName}
            onChange={e => handleChange('weighbridgeName', e.target.value)}
            error={!!errors.weighbridgeName}
            helperText={errors.weighbridgeName}
            fullWidth
            required
          />

          <TextField
            label="Admin Email Address"
            value={formData.adminEmailAddress}
            onChange={e => handleChange('adminEmailAddress', e.target.value)}
            error={!!errors.adminEmailAddress}
            helperText={errors.adminEmailAddress}
            fullWidth
          />

          <TextField
            label="Accounts Email Address"
            value={formData.accountsEmailAddress}
            onChange={e => handleChange('accountsEmailAddress', e.target.value)}
            error={!!errors.accountsEmailAddress}
            helperText={errors.accountsEmailAddress}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          {editData ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddEditWeighbridgeModal;
